import type { Project } from '@/pages/projects'

// Domain-specific error code ranges
const ERROR_RANGES = {
  PROFILE: { start: 1000, end: 1999 },
  PLAN: { start: 3000, end: 3099 },
  SUBSCRIPTION: { start: 3100, end: 3199 },
  SESSION: { start: 3200, end: 3299 },
  STRIPE: { start: 3300, end: 3399 },
  RECIPE: { start: 4000, end: 4999 },
} as const

export enum ErrorCodes {
  // Profile-related errors
  PROFILE_NOT_FOUND = 1001,

  // Plan-related errors
  PLAN_NOT_FOUND = 3001,
  PLAN_NOT_PURCHASABLE = 3009,
  PLAN_EXIST = 3010,

  // Subscription-related errors
  SUBSCRIPTION_NOT_FOUND = 3002,
  SUBSCRIPTION_ALREADY_EXISTS = 3003,
  INCOMPLETE_SUBSCRIPTION = 3007,
  INVALID_SUBSCRIPTION = 3008,

  // Session-related errors
  SESSION_EXIST = 3004,
  MULTIPLE_SESSIONS = 3005,
  INVALID_SESSION = 3006,

  // Stripe-related errors
  STRIPE_ERROR = 3011,

  // Recipe-related errors
  RECIPE_CALIBRATION_INVALID_DATA = 4001,
  RECIPE_GENERATION_FAILED = 4002,
  RECIPE_DEHAZE_INVALID_DATA = 4101,
}

// Error messages mapping
export const ErrorMessages: Record<string, string> = {
  [ErrorCodes.PROFILE_NOT_FOUND]: 'Profile not found',
  [ErrorCodes.PLAN_NOT_FOUND]: 'Plan not found',
  [ErrorCodes.PLAN_NOT_PURCHASABLE]: 'Plan is not available for purchase',
  [ErrorCodes.PLAN_EXIST]: 'Plan already exists',
  [ErrorCodes.SUBSCRIPTION_NOT_FOUND]: 'Subscription not found',
  [ErrorCodes.SUBSCRIPTION_ALREADY_EXISTS]: 'Subscription already exists',
  [ErrorCodes.INCOMPLETE_SUBSCRIPTION]: 'Subscription is incomplete',
  [ErrorCodes.INVALID_SUBSCRIPTION]: 'Invalid subscription',
  [ErrorCodes.SESSION_EXIST]: 'Session already exists',
  [ErrorCodes.MULTIPLE_SESSIONS]: 'Multiple active sessions detected',
  [ErrorCodes.INVALID_SESSION]: 'Invalid session',
  [ErrorCodes.STRIPE_ERROR]: 'Stripe payment processing error',
  [ErrorCodes.RECIPE_CALIBRATION_INVALID_DATA]:
    'Invalid calibration data for recipe',
  [ErrorCodes.RECIPE_GENERATION_FAILED]: 'Failed to generate recipe',
  [ErrorCodes.RECIPE_DEHAZE_INVALID_DATA]: 'Invalid dehaze data for recipe',
}
export const GENERAL_ERROR = 'Something went wrong.'

// Environment variables type definition
interface EnvConfig {
  BASE_URL: string
  CLIENT_ID: string
  DOMAIN: string
  PUSHER_KEY: string
  PUSHER_CLUSTER: string
  INTERCOM_APPID: string
  MAINTAINANCE_MODE: string
  SENTRY_KEY: string
  SENTRY_ENVIRONMENT: string
}

// Type-safe environment variables
export const env: EnvConfig = {
  BASE_URL: import.meta.env.VITE_BASE_URL,
  CLIENT_ID: import.meta.env.VITE_CLIENT_ID,
  DOMAIN: import.meta.env.VITE_DOMAIN,
  PUSHER_KEY: import.meta.env.VITE_PUSHER_KEY,
  PUSHER_CLUSTER: import.meta.env.VITE_PUSHER_CLUSTER,
  INTERCOM_APPID: import.meta.env.VITE_INTERCOM_APPID,
  MAINTAINANCE_MODE: import.meta.env.VITE_MAINTAINANCE_MODE,
  SENTRY_KEY: import.meta.env.VITE_SENTRY_KEY,
  SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
}

// Layout constants with proper units
export const NAVBAR_HEIGHT = 66 as const
export const GALLERY_WIDTH = 311 as const
export const RIGHT_SIDEBAR_WIDTH = 325 as const
export const CASE_CARD_HEIGHT = 56 as const
export const STUMP_SHADE_PANEL_WIDTH = 778 as const
export const WORKFLOW_TAB_HEIGHT = 37 as const

// Helper function to check if an error code belongs to a specific domain
export const isErrorInDomain = (
  code: ErrorCodes,
  domain: keyof typeof ERROR_RANGES,
): boolean => {
  const range = ERROR_RANGES[domain]
  return code >= range.start && code <= range.end
}

// Helper function to get error message
export const getErrorMessage = (code: ErrorCodes): string => {
  return ErrorMessages[code] || 'Unknown error'
}

export enum REGION {
  CERVICAL = 'region.cervical',
  MIDDLE = 'region.middle',
  INCISAL = 'region.incisal',
}

export enum DIGITAL_REGION {
  CERVICAL = 'cervical',
  MIDDLE = 'middle',
  INCISAL = 'incisal',
}

export enum REGIONS {
  CERVICAL = 'CERVICAL',
  MIDDLE = 'MIDDLE',
  INCISAL = 'INCISAL',
}

export enum SLIDER_CLASS {
  CERVICAL = 'slider-1',
  INCISAL = 'slider-2',
  MIDDLE = 'slider-3',
}

export const horizontalLabels = {
  0.5: '0.5mm',
  0.8: '0.8mm',
  1.0: '1.0mm',
  1.2: '1.2mm',
  1.5: '1.5mm',
}

export const sliderRange = [
  { value: 0.5, step: 1 },
  { value: 0.8, step: 2 },
  { value: 1.0, step: 3 },
  { value: 1.2, step: 4 },
  { value: 1.5, step: 5 },
]

export const sliderAvailableValues = [0.5, 0.8, 1.0, 1.2, 1.5]

export const ITEM_HEIGHT = 48
export const ITEM_PADDING_TOP = 8

export const DIGITAL_TRY_IN_AREAS = {
  CERVICAL: 'cervical',
  MIDDLE: 'middle',
  INCISAL: 'incisal',
}

export const illuminants = [0.96422, 1, 0.82521]

export const DEHAZE_OPTIONS = [
  { name: 'high_dehaze', value: 'gallery.high' },
  { name: 'low_dehaze', value: 'gallery.low' },
]

export const pieChartSections = [
  { key: 'cervical', label: 'Cervical' },
  { key: 'middle', label: 'Middle' },
  { key: 'incisal', label: 'Incisal' },
]

export const GREYCARD = 'grey_card'
export const GREY_CARD_OPTIONS = [
  { text: 'L*76', value: 76, disabled: false },
  { text: 'L*79', value: 79, disabled: false },
  { text: 'L*80', value: 80, disabled: false },
]
export const CALIBRATION = {
  greyCard: {
    title: 'calibration.greyCard.title',
    tooltip: 'calibration.greyCard.tooltip',
  },
  calibrate: {
    title: 'calibration.calibrate.title',
    tooltip: 'calibration.calibrate.tooltip',
  },
}

export const DEHAZE_INCLUDED_PATHS = ['/digital-try-in']
export const DUAL_CANVAS_INCLUDED_PATHS = [
  '/digital-try-in',
  '/staining-studio/crown',
  '/staining-studio/stump-shade',
  '/full-layering/staining-corrections',
  '/micro-layering/staining',
  '/full-monolithic/staining',
  '/pfm-crown/staining-corrections',
  '/model-making/3d-printed-model-staining',
  '/matisse-lite/staining-studio',
  '/staining-studio/wash-bake',
  '/full-layering/wash-bake',
  '/pfm-crown/opaquer-staining',
]
export const PHOTO_SUPPORTED_PATH = [
  '/shade-guide-ai',
  '/digital-try-in',
  '/projects',
]

export const SHADE_GUIDE_STEPS = [
  { label: 'shadeGuideAI.shadeGuideSteps.color', id: 'color' },
  { label: 'shadeGuideAI.shadeGuideSteps.material', id: 'material' },
  { label: 'shadeGuideAI.shadeGuideSteps.recipe', id: 'recipe' },
]

export const SHADE_GUIDE_OUTPUT_TABS = [
  { conditionKey: 'aestheticmodel_middle', label: 'Stump Shade' },
  { method: 'staining', label: 'Staining' },
  { method: 'layering', label: 'Wash Bake' },
  { conditionKey: 'ceramic_recipe_middle', label: 'Dentin' },
  { conditionKey: 'enameloptions_middle', label: 'Enamel' },
]

export const RECIPE_TITLE = {
  'all-in-one': 'All-in-one Recipe',
  'shade-guide-ai': 'Shade Guide AI',
  'digital-try-in': 'Digital Try-In',
  'staining-studio/crown': 'Staining Studio: Monolithic/ Color Corrections',
  'staining-studio/gingiva-staining': 'Staining Studio: Gingiva',
  'staining-studio/stump-shade': 'Staining Studio: Stumpshade',
  'full-layering/framework': 'Full Layering',
  'staining-studio/wash-bake': 'Staining Studio: Wash Bake',
  'micro-layering/material-advice': 'Micro Layering',
  'full-monolithic/material-advice': 'Full Monolithic',
  'full-layering/stump-shade': 'Full Layering',
  'micro-layering/stump-shade': 'Micro Layering',
  'full-monolithic/stump-shade': 'Full Monolithic',
  'full-layering/stump-shade-staining': 'Full Layering',
  'micro-layering/stump-shade-staining': 'Micro Layering',
  'full-monolithic/stump-shade-staining': 'Full Monolithic',
  'full-layering/wash-bake': 'Full Layering',
  'pfm-crown/opaquer-staining': 'PFM Crown',
  'pfm-crown/dentin': 'PFM Crown',
  'full-layering/dentin': 'Full Layering',
  'full-layering/internal-effects': 'Full Layering',
  'micro-layering/internal-effects': 'Micro Layering',
  'pfm-crown/internal-effects': 'PFM Crown',
  'full-layering/staining-corrections': 'Full Layering',
  'micro-layering/staining': 'Micro Layering',
  'full-monolithic/staining': 'Full Monolithic',
  'pfm-crown/staining-corrections': 'PFM Crown',
  'gingiva/staining': 'Gingiva',
  'full-layering/enamel': 'Full Layering',
  'micro-layering/enamel': 'Micro Layering',
  'pfm-crown/enamel': 'PFM Crown',
  'gingiva/ceramic': 'Gingiva',
  'pfm-crown/opaquer': 'PFM Crown',
  'model-making/3d-printed-model-staining': 'Model Making',
  'model-making/resin-recipe': 'Model Making',
  'matisse-lite/staining-studio': 'Matisse Lite: Stain & Glaze',
  'matisse-lite/dentin_enamel': 'Matisse Lite: Dentin & Enamel',
}

// Supported image file types with proper typing
export const getSupportedImagetypes = (path?: string) =>
  [
    'optishade',
    'cr2',
    'nef',
    'dng',
    'crw',
    'raf',
    '3fr',
    'kdc',
    'mrw',
    'rwl',
    'orf',
    'rw2',
    'ptx',
    'srw',
    'arw',
    'x3f',
    'cr3',
    ...(path && PHOTO_SUPPORTED_PATH?.includes(path)
      ? ['jpeg', 'png', 'jpg', 'heic']
      : []),
  ] as const

export const PIE_COLORS = [
  '#6BE2BE',
  '#FFB572',
  '#90CAF9',
  '#F48FB1',
  '#B196FF',
  '#72D2FF',
  '#FF6767',
]

export const GINGIVA_PIE_COLORS = [
  '#8FCAF5',
  '#F391B1',
  '#6be2be',
  '#FFB572',
  '#B196FF',
  '#72D2FF',
  '#FF6767',
]

export const STUMP_SHADE_BACKGROUNDS: Record<string, string> = {
  target_cervical: '#6BE2BE',
  target_middle: '#FFB572',
  target_incisal: '#F48FB1',
  preparation_cervical: '#EA4560',
  preparation_middle: '#31D19E',
  preparation_incisal: '#0068FF',
}

export const DENTIN_BACKGROUNDS: Record<string, string> = {
  CERVICAL: '#68CEA1',
  MIDDLE: '#FFB975',
  INCISAL: '#FF9DBF',
}

const INTERNAL_EFFECT_STYLES = [
  {
    keys: ['Mamelon', 'mamelon_halo'],
    background: 'linear-gradient(90deg, #FAFF89 0%, #FC9060 100%)',
    badge: 'MM',
    label: 'Mamelon',
  },
  {
    keys: ['Light Absorption', 'light_absorption', 'Light_Absorption'],
    background: 'linear-gradient(90deg, #AFCBCB 0%, #B478A1 100%)',
    badge: 'LA',
    label: 'Light Absorption',
  },
  {
    keys: ['Light Reflection', 'light_reflection', 'Light_Reflection'],
    background: 'linear-gradient(90deg, #F0F9A7 0%, #32CEB1 100%)',
    badge: 'LR',
    label: 'Light Reflection',
  },
  {
    keys: [
      'Cervical Translucency',
      'cervical_translucency',
      'Cervical_Translucency',
    ],
    background: 'linear-gradient(90deg, #FFC297 0%, #FF6C6C 100%)',
    badge: 'CT',
    label: 'Cervical Translucency',
  },
  {
    keys: ['Enamel Canvas', 'enamel_canvas', 'Enamel_Canvas'],
    background: 'linear-gradient(90deg, #CE81FF 0%, #3FD8FF 100%)',
    badge: 'EC',
    label: 'Enamel Canvas',
  },
  {
    keys: ['Opalesence', 'opalesence', 'opalescence', 'Opalescence'],
    background: 'linear-gradient(90deg, #49C4FF 0%, #BFF9E1 100%)',
    badge: 'OP',
    label: 'Opalesence',
  },
] as const

export const INTERNAL_EFFECTS_BACKGROUND: Record<string, string> = {}
export const INTERNAL_EFFECTS_RENDER_VALUE: Record<string, string> = {}
export const INTERNAL_EFFECTS_LABEL: Record<string, string> = {}

for (const style of INTERNAL_EFFECT_STYLES) {
  for (const key of style.keys) {
    INTERNAL_EFFECTS_BACKGROUND[key] = style.background
    INTERNAL_EFFECTS_RENDER_VALUE[key] = style.badge
    INTERNAL_EFFECTS_LABEL[key] = style.label
  }
}

export const ENAMEL_BACKGROUNDS: Record<string, string> = {
  cervical: 'linear-gradient(90deg, #FF7DCC 0%, #FFC77E 100%)',
  middle: 'linear-gradient(90deg, #F8B1AB 0%, #8EE6BD 100%)',
  incisal: 'linear-gradient(90deg, #62F7D7 0%, #FFC77E 100%)',
}

export enum GINGIVA_SLIDER_CLASS {
  region1 = 'slider-1',
  region2 = 'slider-2',
  region3 = 'slider-3',
}

export const GINGIVA_SLIDERRANGE = [
  { value: 0.5, step: 1 },
  { value: 1.0, step: 2 },
  { value: 1.5, step: 3 },
  { value: 2.0, step: 4 },
  { value: 2.5, step: 5 },
  { value: 3.0, step: 6 },
  { value: 3.5, step: 7 },
  { value: 4.0, step: 8 },
  { value: 4.5, step: 9 },
  { value: 5.0, step: 10 },
]

export const GINGIVA_LABELS = {
  0.5: '0.5',
  1.0: '1.0',
  1.5: '1.5',
  2.0: '2.0',
  2.5: '2.5',
  3.0: '3.0',
  3.5: '3.5',
  4.0: '4.0',
  4.5: '4.5',
  5.0: '5.0',
}

export const OPAQUER_BACKGROUNDS: Record<string, string> = {
  CERVICAL: '#68CEA1',
  MIDDLE: '#FFB975',
  INCISAL: '#FF9DBF',
}

export const REFRENCE_OPTIONS = [
  { value: 'social', text: 'Social Media' },
  { value: 'matisse', text: 'Matisse course/lecture' },
  { value: 'smile-line', text: 'Smile line dealer' },
  { value: 'other', text: 'Friend/colleague' },
]

export const PLAN_TEXTGRADIENT = [
  'linear-gradient(90deg, #A0D911 0%, #5CDBD3 100%)',
  'linear-gradient(90deg, #FFC570 0%, #93D9FF 100%)',
  'linear-gradient(90deg, #FFA6A6 0%, #FFD990 100%)',
  'linear-gradient(90deg, #6FEEFF 0%, #C099FF 100%)',
]

export const statusLabelMap: Record<Project['status'], string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  archive: 'Archived',
}

export const statusOptions: Project['status'][] = [
  'active',
  'scheduled',
  'archive',
]

export const sortingData = [
  { id: 1, name: 'projectsTable.filter.newestUpload', value: '-created_ts' },
  { id: 2, name: 'projectsTable.filter.oldestUpload', value: 'created_ts' },
  { id: 3, name: 'A-Z', value: 'key_raw' },
  { id: 4, name: 'Z-A', value: '-key_raw' },
]

export const Features = {
  GENERIC: 'generic',
  INTERNAL: 'internal',
  AESTHETIC_MODEL: 'aesthetic_model',
  ENAMEL: 'enamel',
  OPAQUER: 'opaquer',
  DENTIN: 'dentin',
  FRAMEWORK: 'framework',
  DIGITAL_TRY_IN: 'digital_try_in',
  FRAMEWORK_STAINING: 'framework_staining',
  COLOR_CHECKER: 'color_checker_digital_try_in',
  ENAMEL_STUDIO: 'enamel_studio_digital_try_in',
  DEHAZING: 'dehazing_digital_try_in',
  STAINING_STUDIO: 'staining_studio',
  COLORMODELSTAINING: 'staining_studio',
  CASE_MANAGEMENT: 'case_management',
  CAMERA_RAW_IMAGE: 'camera_raw_image',
  SHADEGUIDEAI: 'analog_method',
  GINGIVA: 'gingiva',
  GINGIVA_STAINING: 'gingiva',
  MATISSE_LITE: 'matisse_lite',
}

export const Workflows = {
  FULL_LAYERING: 'full_layering',
  MICRO_LAYERING: 'micro_layering',
  FULL_MONOLITHIC: 'full_monolithic',
  GINGIVA: 'gingiva',
  PFM_CROWN: 'pfm_crown',
  MODEL_MAKING: 'model_making',
  STAINING_STUDIO: 'staining_studio',
}

export const RECIPE_WORKFLOW_COLOR = [
  {
    full_layering: 'rgba(227, 160, 8, 1)',
    name: 'Full Layering',
  },
  {
    micro_layering: '#31C48D',
    name: 'Micro Layering',
  },
  {
    full_monolithic: '#16BDCA',
    name: 'Full Monolithic',
  },
  {
    gingiva: '#6875F5',
    name: 'Gingiva',
  },
  {
    pfm_crown: '#9061F9',
    name: 'PFM Crown',
  },
  {
    model_making: 'rgba(255, 90, 31, 1)',
    name: 'Model Making',
  },
  {
    staining_studio: 'rgba(255, 90, 31, 1)',
    name: 'Staining Studio',
  },
]

export const RECIPE_TYPE_OPTIONS = [
  { value: 'generic', label: 'All-In-One' },
  { value: 'internal', label: 'Internal Effects' },
  { value: 'aesthetic_model', label: 'Resin Recipe' },
  { value: 'enamel', label: 'Enamel' },
  { value: 'opaquer', label: 'Opaquer' },
  { value: 'dentin', label: 'Dentin' },
  { value: 'framework_crown', label: 'Framework' },
  { value: 'staining_layering', label: 'Wash Bake' },
  { value: 'staining_fullmonolithic', label: 'Stain & Glaze' },
  { value: 'staining_color_model', label: '3D Printed Model Staining' },
  { value: 'analog_monolithic', label: 'ShadeGuide AI' },
  { value: 'analog_layering', label: 'ShadeGuide AI' },
  { value: 'gingiva', label: 'Gingiva' },
  { value: 'staining_gingiva', label: 'Gingiva' },
  { value: 'framework_veneer', label: 'Framework' },
  { value: 'framework_maryland_bridge', label: 'Framework' },
  { value: 'framework_implant_crown', label: 'Framework' },
  { value: 'framework_monolithic_crown', label: 'Framework' },
  { value: 'framework_implant_monolithic', label: 'Framework' },
  { value: 'matisse_lite', label: 'Dentin & Enamel' },
]

const permissionsMap = {
  owner: {
    canChangeProjectName: true,
    canChangeDentistName: true,
    canChangeDescription: true,
    canShare: true,
    canChangeStatus: true,
    canAddImages: true,
    canView: true,
    canDownload: true,
    canDeleteImages: true,
    canStartTask: true,
    canDuplicate: true,
    canMoveImages: true,
    canDownloadRecipes: true,
    canEditRecipes: true,
    canDeleteRecipes: true,
    canEditImageNote: true,
    canEditDescription: true,
  },
  viewer: {
    canChangeProjectName: false,
    canChangeDentistName: false,
    canChangeDescription: false,
    canShare: false,
    canChangeStatus: false,
    canAddImages: false,
    canView: true,
    canDownload: true,
    canDeleteImages: false,
    canStartTask: false,
    canDuplicate: false,
    canMoveImages: false,
    canDownloadRecipes: true,
    canEditRecipes: false,
    canDeleteRecipes: false,
    canEditImageNote: false,
    canEditDescription: false,
  },
  editor: {
    canChangeProjectName: false,
    canChangeDentistName: false,
    canChangeDescription: false,
    canShare: false,
    canChangeStatus: false,
    canAddImages: true,
    canView: true,
    canDownload: true,
    canDeleteImages: true,
    canStartTask: true,
    canDuplicate: true,
    canMoveImages: true,
    canDownloadRecipes: true,
    canEditRecipes: true,
    canDeleteRecipes: true,
    canEditImageNote: true,
    canEditDescription: true,
  },
} as const

export const getProjectPermissions = (role?: keyof typeof permissionsMap) =>
  permissionsMap[role || 'owner']

export const showPrepColorPicker = [
  'Crown (layering)',
  'Veneer (layering)',
  'Full Monolithic Crown/Veneer',
]

export const RECIPE_TYPE = {
  recipeType: {
    label: 'resinRecipe.recipeType.label',
    tooltip: 'resinRecipe.recipeType.tooltip',
    options: [
      {
        value: 'Single Zone',
        label: 'resinRecipe.recipeType.options.singleZone',
      },
      {
        value: 'Triple Zone',
        label: 'resinRecipe.recipeType.options.tripleZone',
      },
    ],
  },
}

export const SLIDERRANGE = [
  { value: 0.5, text: '0.5mm', step: 1 },
  { value: 0.8, text: '0.8mm', step: 2 },
  { value: 1.0, text: '1.0mm', step: 3 },
  { value: 1.2, text: '1.2mm', step: 4 },
  { value: 1.5, text: '1.5mm', step: 5 },
]

export const REGION_WITH_COLOR = {
  Cervical: 'linear-gradient(90deg, #FFBF95 0%, #FF6F6D 100%)',
  Middle: 'linear-gradient(90deg, #F8B1AB 0%, #8EE6BD 100%)',
  Incisal: 'linear-gradient(90deg, #C3D4FF 0%, #F4BCCD 100%)',
}

// ─── Image Label Feature ────────────────────────────────────────────────────

export interface ImageLabel {
  id: string
  apiId?: number // numeric id used in API payloads (matches Label.id from server)
  name: string
  description?: string
  isPredefined: boolean
}

export const PREDEFINED_IMAGE_LABELS: ImageLabel[] = [
  {
    id: 'preparation',
    name: 'Preparation',
    description: 'Photo of the prepared tooth',
    isPredefined: true,
  },
  {
    id: 'target-tooth',
    name: 'Target Tooth',
    description: 'Photo of the adjacent/reference tooth for shade matching',
    isPredefined: true,
  },
  {
    id: 'gingiva',
    name: 'Gingiva',
    description: 'Photo of the reference gingiva for shade matching',
    isPredefined: true,
  },
  {
    id: 'color-model',
    name: 'Color Model',
    description: 'Photo of the color model before die/stump shade adjustment',
    isPredefined: true,
  },
  {
    id: 'color-model-stained',
    name: 'Color Model: Stained',
    description: 'Photo of the color model after die/stump shade adjustment',
    isPredefined: true,
  },
  {
    id: 'framework',
    name: 'Framework',
    description:
      'Photo of the framework on the model before staining or layering',
    isPredefined: true,
  },
  {
    id: 'wash-bake',
    name: 'Wash Bake',
    description: 'Photo of the framework on the model after the wash bake',
    isPredefined: true,
  },
  {
    id: 'dentin-bake',
    name: 'Dentin Bake',
    description: 'Photo of the crown after the dentin (first) bake',
    isPredefined: true,
  },
  {
    id: 'stain-correction-bake',
    name: 'Stain/Correction Bake',
    description: 'Photo of the crown after the staining or correction bake',
    isPredefined: true,
  },
  {
    id: 'enamel-bake',
    name: 'Enamel Bake',
    description: 'Photo of the crown after the enamel bake',
    isPredefined: true,
  },
  {
    id: 'finished-crown-on-model',
    name: 'Finished Crown on Model',
    description:
      'Photo of the finished crown after the glaze bake or manual polish',
    isPredefined: true,
  },
  {
    id: 'finished-crown-in-mouth',
    name: 'Finished Crown in Mouth',
    description: "Photo of the finished crown seated in the patient's mouth",
    isPredefined: true,
  },
]
